import React from 'react';
import { Box, Stack } from '@solvhealth/jigsaw-v1';
import Root from '../Root';
import SwimLanes from '../SwimLanes';
import EmptyState from './components/EmptyState';
import InsuranceCard from './components/InsuranceCard';
import EntryPointCard from '../Insurance/components/EntryPoint/Card';
import ModalController from './components/ModalController';
import InsuranceHeadline from './components/HeadlineBanner';
import useSetTitle from '../../../../hooks/useSetTitle';
import useSelectUserProfile from '../../hooks/useSelectUserProfile';
import useShouldShowEmptyState from './hooks/useShouldShowEmptyState';
import useSelectInsuranceProfile from '../../hooks/useSelectInsuranceProfile';
import useSetLoadedInsuranceCookie from './hooks/useSetLoadedInsuranceCookie';
import { CategoryIds } from '../SwimLanes/util/tileEnums';
import { isNativeApp } from '../../../../core/util/device';
import { MobileBottomSpacing } from '../../constants/styles';
import { isEmptyObject } from '../../../../core/util/object';
import ObserverContainer from '~/components/SolvPatternLibrary/Profilers/Observer';
import { NativePrompts } from '~/core/util/native';
import { PUSH_OPT_IN_ORIGIN_INSURANCE } from '../../../../constants';
import { colors } from '~/constants/colors';

type Props = {
  params?: any;
};

const Insurance = (props: Props) => {
  const { params } = props;
  const { userProfileId } = params;
  const userProfile = useSelectUserProfile(userProfileId);
  const insuranceProfile = useSelectInsuranceProfile(userProfileId);
  const insuranceProfileError = insuranceProfile?.eligibility_reject_reason_type;
  const missingEligibilityResult = isEmptyObject(insuranceProfile?.eligibility_result);
  const insuranceProfileErrorDismissed = insuranceProfile?.is_eligibility_rejection_error_dismissed;

  useSetLoadedInsuranceCookie();
  useSetTitle('Insurance Benefits');

  const showPrompts = () => {
    const hasAsked = NativePrompts.askForPushPermission(PUSH_OPT_IN_ORIGIN_INSURANCE);
    if (!hasAsked) {
      NativePrompts.askForNativeStoreRating();
    }
  };

  return (
    <Root>
      {useShouldShowEmptyState(userProfileId) ? (
        <EmptyState userProfileId={userProfileId} />
      ) : (
        <>
          <Box position="relative">
            <Box
              // TODO: 'grapeLight' is surprisingly missing from Jigsaw
              backgroundColor={colors.grapeLight as any}
              height="252px"
              position="absolute"
              top={0}
              width="100%"
              zIndex={-1}
            />
            <Box maxWidth="1200px" mx="auto">
              <Stack
                alignItems="left"
                flexDirection={{ _: 'column', md: 'row-reverse' }}
                justifyContent={{ _: 'flex-start', md: 'flex-end' }}
                mx={{ _: 3, md: 0 }}
                pb={3}
                pl={{ _: 0, md: 4 }}
                pt={{ _: 2, md: 4 }}
                style={{
                  gap: '32px',
                }}
                width={{
                  _: '344px',
                  md: 'auto',
                }}
              >
                <InsuranceHeadline userProfileId={userProfileId} />
                <InsuranceCard userProfileId={userProfileId} />
              </Stack>
            </Box>
          </Box>
          {/* This 1200px does not match our application breakpoints, but does match the 1200px
          max width referenced repeatedly in our /account pages */}
          <Box maxWidth="1200px" mx="auto" px={{ _: 1, sm: 0 }}>
            {!insuranceProfileError &&
              !missingEligibilityResult &&
              !insuranceProfileErrorDismissed && (
                <SwimLanes
                  categoryIds={[
                    CategoryIds.DEDUCTIBLE_PROGRESS,
                    CategoryIds.COPAY_COINSURANCE,
                    CategoryIds.OUT_OF_POCKET_MAX,
                  ]}
                  userProfile={userProfile}
                />
              )}

            <EntryPointCard userProfileId={userProfileId} />
          </Box>
          <ObserverContainer handleSawComponent={showPrompts}>
            <div></div>
          </ObserverContainer>
          {isNativeApp() && <MobileBottomSpacing />}
        </>
      )}

      <ModalController userProfileId={userProfileId} />
    </Root>
  );
};

export default Insurance;
